import React from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "text";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  title?: string;
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-blue-700 text-white hover:bg-blue-800 focus:ring-gray-300 disabled:bg-gray-300 disabled:text-white",
  secondary:
    "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 disabled:bg-white disabled:text-gray-400",
  ghost: "bg-transparent text-gray-800 hover:bg-gray-50 disabled:text-gray-400",
  danger: "bg-red-600 text-white hover:bg-red-500 focus:ring-red-300 disabled:bg-red-300",
  text: "bg-transparent text-gray-800 hover:bg-gray-50 disabled:text-gray-400",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1.5 text-sm rounded-md",
  md: "px-3 py-2.5 text-sm rounded-lg",
  lg: "px-4 py-3 text-base rounded-lg",
};

export default function Button({
  children,
  onClick,
  type = "button",
  className = "",
  dataTestId,
  disabled = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  variant = "primary",
  size = "md",
  title,
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors";

  const classes = [
    base,
    variantClasses[variant],
    sizeClasses[size],
    disabled || isLoading ? "cursor-not-allowed" : "cursor-pointer",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      title={title}
      data-testid={dataTestId}
      className={classes}
      onClick={disabled || isLoading ? undefined : onClick}
      disabled={disabled || isLoading}
    >
      {isLoading && (
        <svg
          className="h-4 w-4 animate-spin text-current"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      )}
      {!isLoading && leftIcon}
      <span>{children}</span>
      {!isLoading && rightIcon}
    </button>
  );
}
